import currency from 'currency.js'
import taxCalculator from 'src/services/pricing/TaxCalculator'

/**
 * Calculator service for calculating price values (subtotal, taxes, total) based on given price and tax inputs.
 */
class PriceCalculator {
  static #instance = null

  constructor () {
    if (!PriceCalculator.#instance) {
      PriceCalculator.#instance = this
    }
    return PriceCalculator.#instance
  }

  /**
   * Calculate subtotal, taxes, and total from a net price.
   * @param {*} price
   * @param {*} taxable
   * @param {*} taxes
   * @param {*} taxExempt
   * @param {*} redemptionAmount
   * @returns
   */
  net (price, taxable, taxes, taxExempt, redemptionAmount) {
    const subtotal = currency(price).subtract(redemptionAmount).value
    const taxResult = taxable && !taxExempt ? taxCalculator.net(subtotal, taxes) : { taxAmount: 0, taxes: [] }
    const taxAmount = taxResult.taxAmount
    const total = currency(subtotal).add(taxAmount).value

    return {
      subtotal,
      taxAmount,
      total,
      taxes: taxResult.taxes
    }
  }

  /**
   * Calculate subtotal, taxes, and total from a gross price.
   * @param {*} price
   * @param {*} taxable
   * @param {*} taxes
   * @param {*} taxExempt
   * @param {*} redemptionAmount
   * @returns
   */
  gross (price, taxable, taxes, taxExempt, redemptionAmount) {
    const expectedTotal = currency(price).subtract(redemptionAmount).value
    const taxResult = taxable ? taxCalculator.gross(expectedTotal, taxes) : { taxAmount: 0, taxes: [] }
    const uncorrectedTaxAmount = taxResult.taxAmount
    const uncorrectedSubtotal = expectedTotal - uncorrectedTaxAmount

    if (taxExempt) {
      return {
        subtotal: uncorrectedSubtotal,
        taxAmount: 0,
        total: uncorrectedSubtotal,
        taxes: []
      }
    }

    const uncorrectedTotal = currency(uncorrectedSubtotal).add(uncorrectedTaxAmount).value
    const totalCorrection = currency(expectedTotal).subtract(uncorrectedTotal).value
    const total = currency(uncorrectedTotal).subtract(totalCorrection).value

    const taxCorrectionResult = taxable ? taxCalculator.correction(expectedTotal, uncorrectedTotal, taxes) : { correction: 0, taxes: [] }
    const taxCorrection = taxCorrectionResult.correction
    const taxAmount = currency(uncorrectedTaxAmount).subtract(taxCorrection).value

    // Subtract totalCorrection to match total correction
    // Add taxCorrection to offset tax correction
    const subtotal = currency(uncorrectedSubtotal).subtract(totalCorrection).add(taxCorrection).value

    return {
      subtotal,
      taxAmount,
      total,
      taxes: taxCorrectionResult.taxes
    }
  }

  /**
   * Calculate a rounded total and corrected subtotal and taxes from a given total.
   * @param {*} price
   * @param {*} taxable
   * @param {*} taxes
   * @param {*} taxExempt
   * @param {*} redemptionAmount
   * @returns
   */
  round (subtotal, taxAmount, total, taxable, taxes, taxExempt) {
    const priceRoundingCentsTarget = 5.0

    const cents = currency(total).cents()
    const roundedCents = Math.round(cents / priceRoundingCentsTarget) * priceRoundingCentsTarget

    const isCentsNegative = cents < 0
    const centsDollarString = `${isCentsNegative ? '-' : ''}0.${_.padStart(Math.abs(cents), 2, '0')}`

    const isRoundedCentsNegative = roundedCents < 0
    const roundedCentsDollarString = `${isRoundedCentsNegative ? '-' : ''}0.${_.padStart(Math.abs(roundedCents), 2, '0')}`

    const roundedTotal = currency(total).subtract(centsDollarString).add(roundedCentsDollarString).value
    const totalCorrection = currency(total).subtract(roundedTotal).value

    if (!taxable || taxExempt) {
      return {
        subtotal: roundedTotal,
        taxAmount: 0,
        total: roundedTotal,
        taxes: []
      }
    }

    const taxCorrectionResult = taxCalculator.correction(roundedTotal, total, taxes)
    const taxCorrection = taxCorrectionResult.correction
    const roundedTaxAmount = currency(taxAmount).subtract(taxCorrection).value

    const roundedSubtotal = currency(subtotal).subtract(totalCorrection).add(taxCorrection).value

    return {
      subtotal: roundedSubtotal,
      taxAmount: roundedTaxAmount,
      total: roundedTotal,
      taxes: taxCorrectionResult.taxes
    }
  }
}

const priceCalculator = new PriceCalculator()
export default priceCalculator
